<script setup lang="ts">
import {
    Clock3,
    Gamepad2,
    Plus,
    RotateCcw,
    ShoppingBag,
    X,
} from 'lucide-vue-next';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { locale, t } from '../i18n';
import { money } from '../state';
import type { BalanceHistory, BalanceRow } from '../types';

/**
 * «Історія операцій» (owner, 2026-10-28): the clock beside «Поповнити» on a network's screen.
 * When the player topped up and how, what they played and bought with it, and the balance after
 * each line, newest first, grouped by day. Two totals on top say where the money went at a glance.
 *
 * Paged (2026-10-29): the list ends in a sentinel, and the sentinel coming into view asks for the
 * next, older page (`more`). A filter that leaves the loaded rows short keeps asking until the
 * screen is full or the history runs out.
 */
const props = defineProps<{
    open: boolean;
    network: string | null;
    history: BalanceHistory | null;
    loading: boolean;
    loadingMore?: boolean;
    moreFailed?: boolean;
}>();

const emit = defineEmits<{ close: []; more: [] }>();

type Filter = 'all' | 'in' | 'out';

const filter = ref<Filter>('all');

watch(
    () => props.open,
    (open) => {
        if (open) {
            filter.value = 'all';
        }
    },
);

const rows = computed(() => props.history?.rows ?? []);

const totals = computed(() => props.history?.totals ?? { in: 0, out: 0 });

const hasMore = computed(() => Boolean(props.history?.next_cursor));

const sheet = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

function askForMore(): void {
    if (hasMore.value && !props.loadingMore && !props.moreFailed) {
        emit('more');
    }
}

/**
 * Watches the sentinel from scratch: a fresh observe reports where it stands now, so a page that
 * did not push it off screen asks for the next one straight away.
 */
function observe(): void {
    observer?.disconnect();
    observer = null;

    if (
        !sheet.value ||
        !sentinel.value ||
        typeof IntersectionObserver === 'undefined'
    ) {
        return;
    }

    observer = new IntersectionObserver(
        (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                askForMore();
            }
        },
        { root: sheet.value, rootMargin: '0px 0px 240px 0px' },
    );
    observer.observe(sentinel.value);
}

watch(
    () => [
        props.open,
        props.history?.next_cursor,
        props.loadingMore,
        filter.value,
    ],
    () => nextTick(observe),
);

onBeforeUnmount(() => observer?.disconnect());

const shown = computed(() =>
    filter.value === 'all'
        ? rows.value
        : rows.value.filter((row) => row.effect === filter.value),
);

const intlLocales: Record<string, string> = {
    uk: 'uk-UA',
    ru: 'ru-RU',
    en: 'en-GB',
};

function dayKey(value: string): string {
    const date = new Date(value);

    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(value: string): string {
    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (dayKey(value) === dayKey(today.toISOString())) {
        return t('Сьогодні');
    }

    if (dayKey(value) === dayKey(yesterday.toISOString())) {
        return t('Вчора');
    }

    return new Intl.DateTimeFormat(intlLocales[locale.value] ?? 'uk-UA', {
        day: 'numeric',
        month: 'long',
        ...(date.getFullYear() !== today.getFullYear()
            ? { year: 'numeric' }
            : {}),
    }).format(date);
}

/** The shown rows in runs of one day each, the day's heading first. */
const days = computed(() => {
    const groups: Array<{ key: string; label: string; rows: BalanceRow[] }> =
        [];

    for (const row of shown.value) {
        const key = dayKey(row.date);
        const last = groups[groups.length - 1];

        if (last?.key === key) {
            last.rows.push(row);
        } else {
            groups.push({ key, label: dayLabel(row.date), rows: [row] });
        }
    }

    return groups;
});

function time(value: string): string {
    return new Intl.DateTimeFormat(intlLocales[locale.value] ?? 'uk-UA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(value));
}

function duration(minutes: number | null): string {
    const total = Math.max(0, minutes ?? 0);
    const hours = Math.floor(total / 60);
    const rest = total % 60;

    if (hours === 0) {
        return t('{m} хв', { m: rest });
    }

    return rest === 0
        ? t('{h} год', { h: hours })
        : t('{h} год {m} хв', { h: hours, m: rest });
}

const methods: Record<string, string> = {
    cash: 'готівкою',
    card: 'карткою',
    online: 'онлайн',
    compensation: 'компенсація',
    bonus: 'бонусами',
};

function title(row: BalanceRow): string {
    switch (row.kind) {
        case 'topup':
            return t('Поповнення');
        case 'session':
            return row.running
                ? t('Грає зараз на {pc}', { pc: row.computer ?? 'ПК' })
                : t('Гра на {pc}', { pc: row.computer ?? 'ПК' });
        case 'purchase':
            return t('Покупка');
        case 'refund':
            return t('Повернення покупки');
        default:
            return t('Поповнення скасовано');
    }
}

/** The grey line under the title: the time, then what it was, then where. */
function meta(row: BalanceRow): string {
    const parts = [time(row.date)];

    if (row.kind === 'session') {
        parts.push(duration(row.minutes));
    } else if (row.kind === 'topup' && row.method && methods[row.method]) {
        parts.push(t(methods[row.method]));
    }

    if (row.description) {
        parts.push(row.description);
    }

    if (row.club) {
        parts.push(row.club);
    }

    return parts.join(' · ');
}

function icon(row: BalanceRow) {
    switch (row.kind) {
        case 'topup':
            return Plus;
        case 'session':
            return Gamepad2;
        case 'purchase':
            return ShoppingBag;
        default:
            return RotateCcw;
    }
}

const filters = computed(
    (): Array<{ value: Filter; label: string }> => [
        { value: 'all', label: t('Усі') },
        { value: 'in', label: t('Поповнення') },
        { value: 'out', label: t('Витрати') },
    ],
);
</script>

<template>
    <Transition name="sheet" appear>
        <div v-if="open" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="$emit('close')"
            />
            <section
                ref="sheet"
                class="basic-sheet history-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="history-title"
                data-test="balance-history"
            >
                <button
                    class="sheet-close"
                    type="button"
                    :aria-label="t('Закрити')"
                    @click="$emit('close')"
                >
                    <X :size="19" />
                </button>

                <span v-if="network" class="eyebrow history-network">{{
                    network
                }}</span>
                <h2 id="history-title">{{ t('Історія операцій') }}</h2>

                <div v-if="loading && !history" class="history-skeleton">
                    <span /><span /><span /><span />
                </div>

                <template v-else-if="history">
                    <div v-if="rows.length" class="history-totals">
                        <div class="history-total is-in">
                            <span class="history-total__label">{{
                                t('Поповнено')
                            }}</span>
                            <span class="history-total__value"
                                >+{{ money(totals.in, locale) }} ₴</span
                            >
                        </div>
                        <div class="history-total is-out">
                            <span class="history-total__label">{{
                                t('Витрачено')
                            }}</span>
                            <span class="history-total__value"
                                >−{{ money(totals.out, locale) }} ₴</span
                            >
                        </div>
                    </div>
                    <p v-if="rows.length" class="history-period">
                        {{ t('За весь час') }}
                    </p>

                    <div
                        v-if="rows.length"
                        class="history-filter"
                        role="tablist"
                        :aria-label="t('Історія операцій')"
                    >
                        <button
                            v-for="option in filters"
                            :key="option.value"
                            class="history-filter__chip"
                            :class="{ 'is-active': filter === option.value }"
                            type="button"
                            role="tab"
                            :aria-selected="filter === option.value"
                            :data-test="`history-filter-${option.value}`"
                            @click="filter = option.value"
                        >
                            {{ option.label }}
                        </button>
                    </div>

                    <p v-if="!rows.length" class="history-empty">
                        <Clock3 :size="20" aria-hidden="true" />
                        {{
                            t(
                                'Поки що жодної операції. Тут з’являться поповнення, ігри та покупки з балансу.',
                            )
                        }}
                    </p>
                    <p
                        v-else-if="!shown.length && !hasMore"
                        class="history-empty"
                    >
                        {{ t('Таких операцій ще немає.') }}
                    </p>

                    <section
                        v-for="day in days"
                        :key="day.key"
                        class="history-day"
                    >
                        <h3 class="history-day__label">{{ day.label }}</h3>
                        <ul class="history-list">
                            <li
                                v-for="row in day.rows"
                                :key="row.id"
                                class="history-row"
                                :class="[`is-${row.kind}`, `is-${row.effect}`]"
                                data-test="history-row"
                            >
                                <span class="history-row__icon">
                                    <component
                                        :is="icon(row)"
                                        :size="18"
                                        :stroke-width="2"
                                    />
                                </span>
                                <span class="history-row__body">
                                    <span class="history-row__title">
                                        {{ title(row) }}
                                        <span
                                            v-if="row.running"
                                            class="history-row__live"
                                            aria-hidden="true"
                                        />
                                    </span>
                                    <span class="history-row__meta">{{
                                        meta(row)
                                    }}</span>
                                </span>
                                <span class="history-row__money">
                                    <strong
                                        >{{ row.effect === 'in' ? '+' : '−'
                                        }}{{
                                            money(row.amount, locale)
                                        }}
                                        ₴</strong
                                    >
                                    <small>{{
                                        t('баланс {amount} ₴', {
                                            amount: money(
                                                row.balance_after,
                                                locale,
                                            ),
                                        })
                                    }}</small>
                                </span>
                            </li>
                        </ul>
                    </section>

                    <div
                        v-if="hasMore"
                        ref="sentinel"
                        class="history-more"
                        data-test="history-more"
                    >
                        <button
                            v-if="moreFailed"
                            class="history-more__retry"
                            type="button"
                            data-test="history-more-retry"
                            @click="emit('more')"
                        >
                            {{ t('Не вдалося завантажити. Спробувати ще') }}
                        </button>
                        <div v-else class="history-skeleton is-more">
                            <span /><span />
                        </div>
                    </div>
                </template>
            </section>
        </div>
    </Transition>
</template>

<style scoped>
.history-sheet {
    /* Clear of Telegram's own «Закрыть» / ⌄ / ⋯ in full screen, with room to spare. */
    max-height: min(84dvh, calc(100dvh - var(--app-safe-area-top) - 48px));
    padding-top: 30px;
    overflow-y: auto;
    overscroll-behavior: contain;
    text-align: left;
}
.history-sheet > h2 {
    margin: 4px 46px 16px 0;
}
.history-network {
    display: block;
    margin-top: 14px;
}
.history-totals {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}
.history-total {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--secondary);
}
.history-total__label {
    color: var(--hint);
    font-size: 12px;
}
.history-total__value {
    overflow: hidden;
    font-size: 19px;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.history-total.is-in .history-total__value {
    color: #22a861;
}
.history-total.is-out .history-total__value {
    color: var(--text);
}
.history-period {
    margin: 8px 0 0;
    color: var(--hint);
    font-size: 12px;
}
.history-filter {
    display: flex;
    gap: 6px;
    margin-top: 16px;
}
.history-filter__chip {
    padding: 7px 14px;
    border: 0;
    border-radius: 999px;
    background: var(--secondary);
    color: var(--text);
    font: inherit;
    font-size: 13px;
    font-weight: 550;
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s;
}
.history-filter__chip.is-active {
    background: var(--accent);
    color: #fff;
}
.history-empty {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 18px 0 0;
    color: var(--hint);
    font-size: 13px;
    line-height: 1.5;
}
.history-empty > svg {
    flex: 0 0 auto;
    color: var(--accent);
}
.history-day {
    margin-top: 18px;
}
.history-day__label {
    margin: 0 0 2px;
    color: var(--hint);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}
.history-list {
    margin: 0;
    padding: 0;
    list-style: none;
}
.history-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 0;
    border-bottom: 1px solid color-mix(in srgb, var(--hint) 12%, transparent);
}
.history-row:last-child {
    border-bottom: 0;
}
.history-row__icon {
    display: grid;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    place-items: center;
    border-radius: 50%;
    background: color-mix(in srgb, var(--hint) 12%, var(--surface));
    color: var(--text);
}
.history-row.is-in .history-row__icon {
    background: color-mix(in srgb, #22a861 14%, var(--surface));
    color: #22a861;
}
.history-row.is-session .history-row__icon {
    background: color-mix(in srgb, var(--accent) 14%, var(--surface));
    color: var(--accent);
}
.history-row__body {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;
}
.history-row__title {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.history-row__live {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    border-radius: 50%;
    background: #22a861;
    animation: history-live 1.4s ease-in-out infinite;
}
.history-row__meta {
    overflow: hidden;
    color: var(--hint);
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.history-row__money {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
}
.history-row__money strong {
    color: var(--text);
    font-size: 14px;
    font-variant-numeric: tabular-nums;
}
.history-row.is-in .history-row__money strong {
    color: #22a861;
}
.history-row__money small {
    color: var(--hint);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
}
.history-skeleton {
    display: grid;
    gap: 10px;
    margin-top: 8px;
}
.history-skeleton span {
    height: 52px;
    border-radius: 14px;
    background: var(--secondary);
    animation: history-pulse 1.2s ease-in-out infinite;
}
.history-skeleton span:first-child {
    height: 70px;
}
.history-more {
    margin-top: 10px;
}
.history-skeleton.is-more span:first-child {
    height: 52px;
}
.history-more__retry {
    width: 100%;
    padding: 12px 14px;
    border: 0;
    border-radius: 14px;
    background: var(--secondary);
    color: var(--accent);
    font: inherit;
    font-size: 14px;
    font-weight: 550;
    cursor: pointer;
}
@keyframes history-pulse {
    50% {
        opacity: 0.5;
    }
}
@keyframes history-live {
    50% {
        opacity: 0.35;
    }
}
</style>
